import {
  RULE_SETTINGS_QUERY_KEY,
  RuleSettingsEditor,
} from '@/components/admin/reglages/rule-settings-editor';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { fetchRuleSettings, ruleSettingsApi } from '@/lib/api';
import { RULE_SETTINGS_ID } from '@matter_extractor/shared';
import { Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Réglages > Règles (spec §8.5, §11.3).
 *
 * The two lists that apply to EVERY session: the global rules served to the
 * interviewer by priority (§10.3), and the guardrails the scribe reads on every
 * turn and the engine acts on before anything else.
 *
 * Since issue #192 (vitrine.md section 5) they are ONE versioned object, « Les
 * règles », a singleton on the layer of the experiences and the texts of the
 * noyaux: the two lists are its DRAFT, « Enregistrer une nouvelle version » its
 * one write - for the rules and the guardrails together, where the screen had
 * a save for the rules and one per guardrail - and a session freezes the
 * version it starts on, so a rule rewritten here never reaches a session that
 * has begun. The history walks back to the version that had a rule.
 *
 * ## « Voir » travels in the URL
 *
 * `?version=<id>` opens a version read-only, the régime of every other editor
 * and for its reason: a version somebody is reading is what gets pasted into a
 * thread. An unknown parameter degrades to « no version ».
 *
 * ## The editor's key is a counter
 *
 * The régime of `reglages.noyaux.tsx`: the editor seeds its draft from the detail
 * ONCE, so a reload of a stale draft has to be a REMOUNT, and a key carrying the
 * draft's stamp would remount under the caret at every autosave.
 */

type ReglesSearch = { version?: string };

function readSearch(search: Record<string, unknown>): ReglesSearch {
  const raw = search.version;
  return typeof raw === 'string' && raw !== '' ? { version: raw } : {};
}

export const Route = createFileRoute('/admin/reglages/regles')({
  validateSearch: readSearch,
  component: ReglagesReglesRoute,
});

function ReglagesReglesRoute() {
  const { version } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <ReglagesReglesPage
      version={version ?? null}
      onView={(versionId) =>
        void navigate({ to: '/admin/reglages/regles', search: { version: versionId } })
      }
      onCloseView={() => void navigate({ to: '/admin/reglages/regles', search: {} })}
    />
  );
}

export interface ReglagesReglesPageProps {
  /** The version « Voir » opened, from `?version=`, or null. */
  readonly version: string | null;
  readonly onView: (versionId: string) => void;
  readonly onCloseView: () => void;
}

/**
 * EXPORTED for `tests/reglages-regles.test.tsx`: `vitest.config.ts` leaves
 * `src/routes/**` out of the coverage REPORT, not out of the suite, and what
 * this page decides - the key of the editor, the read-only view - is invisible
 * from the components below it.
 */
export function ReglagesReglesPage({ version, onView, onCloseView }: ReglagesReglesPageProps) {
  const { email } = useAdminSession();
  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const settings = useQuery({
    queryKey: [RULE_SETTINGS_QUERY_KEY, email, RULE_SETTINGS_ID],
    queryFn: fetchRuleSettings,
    retry: false,
  });

  const viewed = useQuery({
    queryKey: ['rule-settings-version', email, version],
    queryFn: () => ruleSettingsApi.readVersion(RULE_SETTINGS_ID, version ?? ''),
    enabled: version !== null && settings.data !== undefined,
    retry: false,
  });

  // A 401 is the door reopening, not an error of this screen (T6).
  const rejected = useRejectOn401(settings.error);

  return (
    <div className="space-y-6 pb-16">
      <header className="space-y-1">
        <h2 className="font-bold text-bleu-nuit text-h2">Règles</h2>
        <p className="max-w-3xl text-gray-700 text-small">
          Ce qui s’applique à toutes les sessions : les règles globales que l’interviewer reçoit par
          priorité, et les garde-fous que le scribe observe tour par tour.{' '}
          <strong>Un seul objet versionné</strong>, « Les règles » : une session fige au démarrage
          la version qu’elle joue.
        </p>
      </header>

      {settings.isPending && !settings.isError ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement des règles et des garde-fous" />
        </div>
      ) : null}

      {settings.isError && !rejected ? (
        <p role="alert" className="text-rouge-deep text-small">
          Les règles et les garde-fous n’ont pas pu être lus. Rechargez l’écran.
        </p>
      ) : null}

      {settings.data === undefined ? null : (
        <RuleSettingsEditor
          key={`regles-${reload}`}
          detail={settings.data}
          viewing={viewed.data ?? null}
          onView={(summary) => onView(summary.id)}
          onCloseView={onCloseView}
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void settings.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
